/** Pure derivations for the home page. Numbers match the old HomePage, MeetingCard and PerformanceSummaryTiles. */
import { trackTypeOf } from '../chrome';
import { datePath } from '../lib/dates';
import { clockTime } from '../lib/format';
import type { BestBet } from '../common/BestBetTile';

export interface PerformanceImproved {
  total_races: number;
  top_1_picks: number;
  top_1_scratched: number;
  top_1_winners: number;
  top_1_places?: number | null;
  top_1_top_4_places?: number | null;
  top_1_roi: number | null;
  top_2_picks: number;
  top_2_scratched: number;
  top_2_winners: number;
  top_2_places?: number | null;
  top_2_top_4_places?: number | null;
  top_3_picks: number;
  top_3_scratched: number;
  top_3_winners: number;
  top_3_places?: number | null;
  top_3_top_4_places?: number | null;
  value_picks: number | null;
  value_scratched: number | null;
  value_winners: number;
  value_places?: number | null;
  value_top_4_places?: number | null;
  value_roi: number | null;
}

export interface RaceResultSegment {
  race_number: number;
  had_winner: boolean | null;
  top_pick_won: boolean | null;
  value_bet_won: boolean | null;
  quinella_hit: boolean | null;
  trifecta_hit: boolean | null;
  first_four_hit: boolean | null;
  quaddie_hit: boolean | null;
  early_quaddie_hit: boolean | null;
}

/** A meeting as GET_MEETINGS_MINIMAL returns it (the fields the home page reads). */
export interface HomeMeeting {
  meeting_id: string;
  meeting_date: string;
  race_count: number;
  is_private?: boolean | null;
  is_training?: boolean | null;
  track: {
    track_id: string;
    code: string;
    name: string;
    state: string;
    type: string;
    performance?: { top_1_win_percentage?: number | null } | null;
  };
  performanceImproved?: PerformanceImproved | null;
  exoticBets?: { quinellas?: number | null; box_trifectas?: number | null; box_first_fours?: number | null } | null;
  trackConditions?: { trackCondition?: string | null; abandoned?: boolean | null } | null;
  raceResultSegments?: RaceResultSegment[] | null;
}

/** The tips page for a meeting: /:state/:track/:YYYYMMDD, as the old MeetingCard linked. */
export const meetingHref = (m: HomeMeeting): string =>
  `/${m.track.state.toLowerCase().replace(/\s+/g, '')}/${m.track.code.toLowerCase()}/${datePath(new Date(m.meeting_date))}`;

/** One meeting the API names by track and day, for linking to it. */
export interface MeetingRef {
  state?: string | null;
  trackCode?: string | null;
  /** An epoch-milliseconds string (how a date column reaches GraphQL as a String) or a date string. */
  meetingDate?: string | null;
}

/** A meeting's tips page, optionally scrolled to one race; null when the API left anything out. */
export function meetingRefHref(m: MeetingRef | null | undefined, raceNumber?: number | null): string | null {
  if (!m?.state || !m.trackCode || !m.meetingDate) return null;
  const d = new Date(/^\d+$/.test(m.meetingDate) ? Number(m.meetingDate) : m.meetingDate);
  if (isNaN(d.getTime())) return null;
  const base = `/${m.state.toLowerCase().replace(/\s+/g, '')}/${m.trackCode.toLowerCase()}/${datePath(d)}`;
  return raceNumber ? `${base}?scrollToRace=${raceNumber}` : base;
}

/** Metropolitan / Provincial / Country, each sorted by top-pick ROI (highest first), then name. */
export function groupMeetings(meetings: HomeMeeting[] = []) {
  const grouped: Record<'metropolitan' | 'provincial' | 'country', HomeMeeting[]> = { metropolitan: [], provincial: [], country: [] };
  // ponytail: null ROI sorts as 0 and missing performance as -999, exactly as the old page did
  const roi = (m: HomeMeeting) => (m.performanceImproved && m.performanceImproved.top_1_roi !== undefined ? Number(m.performanceImproved.top_1_roi) : -999);
  meetings.forEach((m) => grouped[trackTypeOf(m.track.type) as keyof typeof grouped].push(m));
  const sort = (a: HomeMeeting, b: HomeMeeting) => roi(b) - roi(a) || a.track.name.localeCompare(b.track.name);
  Object.values(grouped).forEach((g) => g.sort(sort));
  return grouped;
}

/** Top-pick ROI pooled across meetings, weighted by non-scratched top picks. */
export function categoryRoi(meetings: HomeMeeting[] = []): number {
  let weighted = 0;
  let picks = 0;
  meetings.forEach((m) => {
    const p = m.performanceImproved;
    if (p && p.top_1_roi != null) {
      const n = p.top_1_picks - p.top_1_scratched;
      if (n > 0) {
        weighted += p.top_1_roi * n;
        picks += n;
      }
    }
  });
  return picks > 0 ? weighted / picks : 0;
}

/** Value-bet ROI pooled the same way; a null ROI means nothing has settled yet, so it is left out. */
export function categoryValueRoi(meetings: HomeMeeting[] = []): number {
  let weighted = 0;
  let picks = 0;
  meetings.forEach((m) => {
    const p = m.performanceImproved;
    if (p && p.value_roi != null) {
      const n = (p.value_picks ?? 0) - (p.value_scratched ?? 0);
      if (n > 0) {
        weighted += p.value_roi * n;
        picks += n;
      }
    }
  });
  return picks > 0 ? weighted / picks : 0;
}

/** Value ROI is hidden when unknown or a total loss, as on the old meeting card. */
export const showValueRoi = (v: number | null | undefined): v is number => v != null && v !== -100;

/** Races run so far (a result bar that is green or red). */
export const racesRun = (m: HomeMeeting): number => (m.raceResultSegments ?? []).filter((s) => s.had_winner != null).length;

/** The numbers on a meeting card. */
export function meetingStats(m: HomeMeeting) {
  const p = m.performanceImproved;
  const segs = m.raceResultSegments ?? [];
  return {
    run: racesRun(m),
    topWinners: p?.top_1_winners ?? 0,
    topPicks: p ? p.top_1_picks - p.top_1_scratched : 0,
    anyWon: segs.filter((s) => s.had_winner === true).length,
    roi: p?.top_1_roi ?? null,
    valueRoi: p?.value_roi ?? null,
  };
}

export interface DayTotals {
  races: number;
  meetings: number;
  topWinners: number;
  allWinners: number;
  top4: number;
  tips: number;
  quinellas: number;
  trifectas: number;
  firstFours: number;
  quaddies: number;
  earlyQuaddies: number;
}

/** The day's totals across meetings, as PerformanceSummaryTiles aggregates them. */
export function dayTotals(meetings: HomeMeeting[]): DayTotals {
  const t: DayTotals = { races: 0, meetings: 0, topWinners: 0, allWinners: 0, top4: 0, tips: 0, quinellas: 0, trifectas: 0, firstFours: 0, quaddies: 0, earlyQuaddies: 0 };
  meetings.forEach((m) => {
    const p = m.performanceImproved;
    const x = m.exoticBets;
    if (!p || !x) return;
    t.races += p.total_races;
    t.meetings += 1;
    t.topWinners += p.top_1_winners;
    t.allWinners += p.top_1_winners + p.top_2_winners + p.top_3_winners + p.value_winners;
    t.top4 +=
      p.top_1_top_4_places !== undefined
        ? (p.top_1_top_4_places || 0) + (p.top_2_top_4_places || 0) + (p.top_3_top_4_places || 0) + (p.value_top_4_places || 0)
        : p.top_1_winners + (p.top_1_places || 0) + p.top_2_winners + (p.top_2_places || 0) + p.top_3_winners + (p.top_3_places || 0) + p.value_winners + (p.value_places || 0);
    t.tips += p.top_1_picks - p.top_1_scratched + (p.top_2_picks - p.top_2_scratched) + (p.top_3_picks - p.top_3_scratched) + ((p.value_picks ?? 0) - (p.value_scratched ?? 0));
    t.quinellas += x.quinellas || 0;
    t.trifectas += x.box_trifectas || 0;
    t.firstFours += x.box_first_fours || 0;
    if (m.raceResultSegments?.some((s) => s.quaddie_hit)) t.quaddies += 1;
    if (m.raceResultSegments?.some((s) => s.early_quaddie_hit)) t.earlyQuaddies += 1;
  });
  return t;
}

export const share = (n: number, of: number): number => (of > 0 ? (n / of) * 100 : 0);

export interface DayTile {
  key: string;
  label: string;
  count: number;
  of: number;
  colour: string;
  help: string;
}

/** The Today-so-far tiles: same wording, colours and shown-when-above-zero rule as PerformanceSummaryTiles. */
export interface DayAgreement {
  /** The brand's name for a selection our analyses agree on, e.g. 'Prime Pick'. */
  markName: string;
  wins: number;
  races: number;
  /** Level-stakes return at SP, as a percentage; null when unknown. */
  roi?: number | null;
}

export function dayTiles(t: DayTotals, agree?: DayAgreement): DayTile[] {
  const all: DayTile[] = [
    { key: 'top', label: 'Top Pick Winners', count: t.topWinners, of: t.races, colour: '#f59e0b', help: 'Our #1 tip selections that won their race. This shows how often our top-rated horse finished first.' },
    ...(agree
      ? [{ key: 'agree', label: `${agree.markName} Winners`, count: agree.wins, of: agree.races, colour: '#d4a017', help: `Our ${agree.markName} selections that won their race, out of those that have run.` }]
      : []),
    { key: 'all', label: 'Total Winners', count: t.allWinners, of: t.races, colour: '#8b5cf6', help: 'Combined winners from all our tip types (Top Pick, 2nd Pick, 3rd Pick, and Value Bet). Shows our overall success rate across all selections.' },
    { key: 'top4', label: 'Top 4 Places', count: t.top4, of: t.tips, colour: '#22c55e', help: 'All our tips that finished in the top 4 positions (1st, 2nd, 3rd, or 4th). Calculated as a percentage of total tips excluding scratched horses.' },
    { key: 'q', label: 'Quinellas', count: t.quinellas, of: t.races, colour: '#3b82f6', help: 'Races where our top 2 picks finished 1st and 2nd in any order.' },
    { key: 't', label: 'Trifectas', count: t.trifectas, of: t.races, colour: '#ef4444', help: 'Races where our top 3 picks finished 1st, 2nd and 3rd in any order (boxed trifecta).' },
    { key: 'f', label: 'First Fours', count: t.firstFours, of: t.races, colour: '#06b6d4', help: 'Races where our top 4 picks finished 1st to 4th in any order (boxed first four).' },
    { key: 'q4', label: 'Quaddie', count: t.quaddies, of: t.meetings, colour: '#8b5cf6', help: 'Meetings where our picks won all four quaddie legs (last 4 races).' },
    { key: 'eq', label: 'Early Quaddie', count: t.earlyQuaddies, of: t.meetings, colour: '#f59e0b', help: 'Meetings where our picks won all four early quaddie legs. For 8+ race meetings these are the middle four races; for 7 race meetings they are races 1-4.' },
  ];
  return all.filter((x) => x.count > 0);
}

/** A dailyBestBets row (the fields the tile needs). */
export interface DailyBestBet {
  raceId: string;
  trackCode?: string | null;
  state?: string | null;
  raceNumber: number;
  horseName: string;
  trackName: string;
  tabNumber: number | string;
  marketOdds: number | null;
  assessedOdds: number | null;
  adjustedAssessedProb: number | null;
  valuePercentage: number | null;
  raceTime?: string | null;
  analysis?: string | null;
  finishPosition?: number | null;
  isConsensusPick?: boolean | null;
}

/** Strips one pair of wrapping quotes from model text, as cleanAnalysisText did. */
const unquote = (s: string | null | undefined): string | null => {
  const t = (s ?? '').trim();
  if (!t) return null;
  return t.length >= 2 && t.startsWith('"') && t.endsWith('"') ? t.slice(1, -1) : t;
};

export function toBestBet(b: DailyBestBet, kind: 'best' | 'value', index: number): BestBet {
  return {
    kind,
    index: index + 1,
    track: b.trackName,
    race: b.raceNumber,
    number: Number(b.tabNumber),
    horse: b.horseName,
    price: b.marketOdds,
    time: b.raceTime ? clockTime(b.raceTime) : undefined,
    edge: !!b.isConsensusPick,
    position: b.finishPosition,
    why: unquote(b.analysis),
    chance: b.adjustedAssessedProb,
    ourPrice: b.assessedOdds,
    overlay: kind === 'value' ? b.valuePercentage : null,
    // the race date comes from the race_id prefix (YYYYMMDD-...)
    href:
      b.state && b.trackCode && /^\d{8}-/.test(b.raceId)
        ? `/${b.state.toLowerCase()}/${b.trackCode.toLowerCase()}/${b.raceId.slice(0, 8)}?scrollToRace=${b.raceNumber}`
        : undefined,
  };
}

/** One row of the upcoming-tips strip, from either the subscriber query or the signed-out teaser. */
export interface UpcomingRow {
  id: string;
  meetingId?: string;
  track: string;
  state: string;
  code: string;
  race: number;
  /** ISO instant. */
  time: string;
  number?: string;
  horse?: string;
  edge?: boolean;
}

interface RawUpcoming {
  race_id: string;
  race_number: number;
  race_time?: string | null;
  meeting?: { meeting_id?: string; track?: { code: string; name: string; state: string } | null } | null;
  analysis?: { horse_id: string; horse_name?: string | null; pick_type: string; pick_order: number; isConsensusPick?: boolean | null }[] | null;
}

/**
 * Races still to run (45 seconds' grace after the jump, as before), soonest first.
 * The subscriber query returns all of today's upcoming races; the page reveals them a few at a time.
 */
export function upcomingRows(races: RawUpcoming[] | null | undefined, now: number): UpcomingRow[] {
  return (races ?? [])
    .filter((r) => r.race_time && r.meeting?.track && new Date(r.race_time).getTime() + 45000 > now)
    .map((r) => {
      const top = r.analysis?.find((a) => a.pick_type === 'top' && a.pick_order === 1);
      const t = r.meeting!.track!;
      return {
        id: r.race_id,
        meetingId: r.meeting?.meeting_id,
        track: t.name,
        state: t.state,
        code: t.code,
        race: r.race_number,
        time: r.race_time!,
        number: top?.horse_id,
        horse: top?.horse_name ?? undefined,
        edge: !!top?.isConsensusPick,
      };
    })
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
}

/** The race's tips page, scrolled to the race. */
export const upcomingHref = (r: UpcomingRow): string =>
  `/${r.state.toLowerCase().replace(/\s+/g, '')}/${r.code.toLowerCase()}/${r.id.split('-')[0]}?scrollToRace=${r.race}`;

/** Each meeting's next race start (ISO), from the upcoming rows: drives the tracks drawer's countdowns. */
export function nextRaceByMeeting(rows: UpcomingRow[]): Map<string, string> {
  const next = new Map<string, string>();
  for (const r of rows) {
    if (!r.meetingId) continue;
    const seen = next.get(r.meetingId);
    if (!seen || Date.parse(r.time) < Date.parse(seen)) next.set(r.meetingId, r.time);
  }
  return next;
}
